import { NgTemplateOutlet } from '@angular/common';
import { Component, inject } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { RaIconComponent } from 'ra-ui';
import { RaEditTooltipDirective } from '../../directives/ra-edit-tooltip/ra-edit-tooltip.directive';
import { MediaLookPanelService } from '../../services/media-look-panel.service';

/**
 * Formato da mídia (Foto, Grade, Vídeo) em modo foco: uma folha na esquerda, por cima do
 * editor escurecido, com a prévia do PDF acesa à direita (ela fica acima do fundo).
 *
 * O conteúdo é o template do menu do card que abriu o painel, então segue ligado a ele: as
 * escolhas salvam pelo card. As fases moram no MediaLookPanelService; aqui fica só o desenho
 * (a fase vai para `data-phase`, que o SCSS anima).
 */
@Component({
    selector: 'media-look-panel',
    templateUrl: 'media-look-panel.component.html',
    styleUrl: 'media-look-panel.component.scss',
    imports: [NgTemplateOutlet, RaEditTooltipDirective, RaIconComponent, TranslatePipe],
    host: {
        '[attr.data-phase]': 'panel.phase()',
        '(document:keydown.escape)': 'onEscape()',
        '(document:pointerdown)': 'onDocumentPointerDown($event)',
    },
})
export class MediaLookPanelComponent {
    public panel = inject(MediaLookPanelService);

    /**
     * Clique fora do painel fecha, como num modal: no fundo escuro, que cobre o editor e o
     * cabeçalho. Não fecham: o próprio painel, a prévia (é ela que mostra o ajuste; o clique
     * dentro da folha nem chega aqui, é outro documento) e o que flutua por cima da página
     * (tooltip, menu, diálogo).
     */
    public onDocumentPointerDown(event: PointerEvent): void {
        if (!this.panel.focused()) return;

        const target = event.target as Element | null;
        if (!target?.isConnected) return;
        if (target.closest('.media-panel, pdf-preview-panel, .cdk-overlay-container, [role="dialog"], [role="menu"]')) return;

        this.panel.close();
    }

    /**
     * Esc fecha, como num modal. Com o editor escurecido não há texto sendo editado por baixo
     * para disputar a tecla; um menu ou diálogo aberto por cima do painel fecha primeiro.
     */
    public onEscape(): void {
        if (!this.panel.focused()) return;
        if (document.querySelector('.cdk-overlay-container [role="menu"], [role="dialog"]')) return;

        this.panel.close();
    }
}
